import {
  addDays,
  addMonths,
  endOfMonth,
  endOfWeek,
  format,
  parseISO,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import type { Filter } from '../types/user';

export type DatePresetId =
  | 'thisWeek'
  | 'nextWeek'
  | 'thisMonth'
  | 'nextMonth'
  | 'currentQuarter'
  | 'nextQuarter'
  | 'currentFiscalYear';

export interface DatePreset {
  id: DatePresetId;
  label: string;
  needsFiscal: boolean;
}

export const DATE_PRESETS: DatePreset[] = [
  { id: 'thisWeek', label: 'Closing This Week', needsFiscal: false },
  { id: 'nextWeek', label: 'Closing Next Week', needsFiscal: false },
  { id: 'thisMonth', label: 'Closing This Month', needsFiscal: false },
  { id: 'nextMonth', label: 'Closing Next Month', needsFiscal: false },
  { id: 'currentQuarter', label: 'Closing Current Quarter', needsFiscal: true },
  { id: 'nextQuarter', label: 'Closing Next Quarter', needsFiscal: true },
  { id: 'currentFiscalYear', label: 'Closing Current Fiscal Year', needsFiscal: true },
];

export interface FiscalPeriod {
  type: 'Quarter' | 'Year';
  start: string; // YYYY-MM-DD
  end: string; // YYYY-MM-DD, inclusive
}

export interface DateRange {
  start: string; // YYYY-MM-DD
  end: string; // YYYY-MM-DD, inclusive
}

const DATE_FORMAT = 'yyyy-MM-dd';
const SUNDAY = 0;

const toIso = (date: Date) => format(date, DATE_FORMAT);

const rangeOf = (start: Date, end: Date): DateRange => ({ start: toIso(start), end: toIso(end) });

const periodContaining = (periods: FiscalPeriod[], type: FiscalPeriod['type'], day: string) =>
  periods.find((p) => p.type === type && p.start <= day && day <= p.end);

/**
 * The inclusive date range a preset covers, or null when it can't be worked out (a fiscal preset
 * without a matching Salesforce period). Weeks run Sunday to Saturday, like Salesforce for US locales.
 */
export const resolveDateRange = (
  id: DatePresetId,
  today: Date,
  periods: FiscalPeriod[] = []
): DateRange | null => {
  switch (id) {
    case 'thisWeek':
      return rangeOf(
        startOfWeek(today, { weekStartsOn: SUNDAY }),
        endOfWeek(today, { weekStartsOn: SUNDAY })
      );
    case 'nextWeek': {
      const nextWeek = addDays(today, 7);
      return rangeOf(
        startOfWeek(nextWeek, { weekStartsOn: SUNDAY }),
        endOfWeek(nextWeek, { weekStartsOn: SUNDAY })
      );
    }
    case 'thisMonth':
      return rangeOf(startOfMonth(today), endOfMonth(today));
    case 'nextMonth': {
      const nextMonth = addMonths(today, 1);
      return rangeOf(startOfMonth(nextMonth), endOfMonth(nextMonth));
    }
    case 'currentQuarter': {
      const current = periodContaining(periods, 'Quarter', toIso(today));
      return current ? { start: current.start, end: current.end } : null;
    }
    case 'nextQuarter': {
      const current = periodContaining(periods, 'Quarter', toIso(today));
      if (!current) return null;
      const nextStart = toIso(addDays(parseISO(current.end), 1));
      const next = periods.find((p) => p.type === 'Quarter' && p.start === nextStart);
      return next ? { start: next.start, end: next.end } : null;
    }
    case 'currentFiscalYear': {
      const year = periodContaining(periods, 'Year', toIso(today));
      return year ? { start: year.start, end: year.end } : null;
    }
    default:
      return null;
  }
};

/**
 * The filters sent to sf/opportunity for a preset: the close date within the range. The backend's
 * before/after are strict, so the bounds are widened by a day. The endpoint itself already restricts
 * the list to open opportunities, so no stage condition is needed.
 */
export const buildPresetFilters = (range: DateRange): Filter[] => [
  { prop: 'CloseDate', operator: 'after', comparison: toIso(addDays(parseISO(range.start), -1)) },
  { prop: 'CloseDate', operator: 'before', comparison: toIso(addDays(parseISO(range.end), 1)) },
];
